import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useTvFreshness, tvLoadErrorMessage } from '../hooks/useTvFreshness';
import { useOnReconnect } from '../hooks/useOnReconnect';
import { formatSavedAt } from '../services/connectionStatus';
import { readTvSnapshot, writeTvSnapshot } from '../utils/tvSnapshot';
import pingolfHero from '../assets/pingolf-hero.webp';
import { pickTvWeek, describeWeekStage, formatVsPar, formatHandicap } from '../utils/pingolfScoring';
import {
  playerName,
  buildCourseStrip,
  buildWeekHighlights,
  interleaveTicker,
  pageSlice
} from '../utils/pingolfTvBoard';
import './PinGolfLeagueTv.css';

const REFRESH_INTERVAL_MS = 20000;
// Nobody is touching this screen, so the standings turn the page on their own. Long enough to
// read ten rows from across the room, short enough that a 30-player field cycles inside a minute.
const PAGE_INTERVAL_MS = 10000;
const ROWS_PER_PAGE = 10;

function formatNextNight(date) {
  const day = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} ${date.getDate()} ${month} · ${time}`;
}

// The next host night still ahead of us, anywhere in the season. Read off the sessions rather
// than the week's own dates so it names the night people will actually turn up to.
function findNextNight(weeks, now) {
  let next = null;
  for (const week of weeks || []) {
    for (const session of week.sessions || []) {
      const time = new Date(session.event_date).getTime();
      if (Number.isNaN(time) || time <= now) continue;
      if (!next || time < next.time) next = { time, weekNumber: week.week_number };
    }
  }
  return next;
}

function PinGolfLeagueTv() {
  const { seasonId } = useParams();
  // The last live data this browser saw, so a reload with the server down
  // still shows the league, marked as not live (utils/tvSnapshot.js).
  const snapshotKey = `pingolf-season-tv-${seasonId}`;
  const [snapshot] = useState(() => readTvSnapshot(snapshotKey));
  const [season, setSeason] = useState(() => snapshot?.data.season ?? null);
  const [standings, setStandings] = useState(() => snapshot?.data.standings ?? []);
  const [weeks, setWeeks] = useState(() => snapshot?.data.weeks ?? []);
  const [week, setWeek] = useState(() => snapshot?.data.week ?? null);
  const [netBoard, setNetBoard] = useState(() => snapshot?.data.netBoard ?? []);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const { stale, asOf, noteResponses } = useTvFreshness(snapshot?.savedAt ?? null);

  const fetchData = useCallback(async () => {
    try {
      const [seasonRes, standingsRes, weeksRes] = await Promise.all([
        apiService.getPingolfSeason(seasonId),
        apiService.getPingolfSeasonStandings(seasonId),
        apiService.getPingolfSeasonWeeks(seasonId)
      ]);
      setSeason(seasonRes.data);
      setStandings(standingsRes.data);
      setWeeks(weeksRes.data);

      const tvWeek = pickTvWeek(weeksRes.data);
      setWeek(tvWeek);
      const netBoardRes = tvWeek ? await apiService.getPingolfWeekNetBoard(tvWeek.id) : null;
      setNetBoard(netBoardRes ? netBoardRes.data : []);
      const allLive = noteResponses([seasonRes, standingsRes, weeksRes, netBoardRes].filter(Boolean));
      if (allLive) {
        writeTvSnapshot(snapshotKey, {
          season: seasonRes.data,
          standings: standingsRes.data,
          weeks: weeksRes.data,
          week: tvWeek,
          netBoard: netBoardRes ? netBoardRes.data : []
        });
      }
      setError(null);
    } catch (err) {
      setError(tvLoadErrorMessage(err, 'Unable to load PinGolf League standings.'));
    }
  }, [seasonId, snapshotKey, noteResponses]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // The server is back: reload now rather than at the next refresh.
  useOnReconnect(fetchData);

  useEffect(() => {
    const intervalId = setInterval(() => setTick(value => value + 1), PAGE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const weekCount = season ? season.week_count : 0;
  const dropCount = season ? season.drop_count : 0;
  const stage = describeWeekStage(week);
  const isFinal = stage === 'final';

  // A week column is "live" once it has a result to show. Future weeks keep their column so
  // the shape of the season is visible, but they sit muted rather than as a wall of dashes.
  const playedWeeks = useMemo(() => {
    const played = new Set();
    for (const seasonWeek of weeks) {
      if (seasonWeek.status === 'finalized') played.add(seasonWeek.week_number);
    }
    for (const entry of standings) {
      (entry.weeklyPoints || []).forEach((points, index) => {
        if (points > 0) played.add(index + 1);
      });
    }
    return played;
  }, [weeks, standings]);

  const ticker = useMemo(() => interleaveTicker(
    buildCourseStrip(week, netBoard),
    buildWeekHighlights(netBoard, week ? week.week_number : null)
  ), [week, netBoard]);

  // Only while there is nothing to show. A failed refresh used to replace
  // standings already on screen with this message.
  if (error && !season) {
    return <div className="pgtv pgtv-message">{error}</div>;
  }
  if (!season) {
    return <div className="pgtv pgtv-message">Loading PinGolf League...</div>;
  }

  const standingsPage = pageSlice(standings, ROWS_PER_PAGE, tick);
  const boardPage = pageSlice(netBoard, ROWS_PER_PAGE, tick);

  const finalizedCount = weeks.filter(seasonWeek => seasonWeek.status === 'finalized').length;
  const progressLabel = stage === 'in progress'
    ? `Week ${week.week_number} in progress`
    : `Week ${finalizedCount} of ${weekCount} complete`;

  const nextNight = findNextNight(weeks, Date.now());
  const countingWeeks = Math.max(weekCount - dropCount, 0);
  // season_pool_split is a JSONB object keyed by prize type (champion, runner_up, ...), and how
  // many keys it carries is the structure the season chose - anything from winner-take-all to
  // the top four. Counting the paying shares is what lets the footer name the real number
  // instead of reciting the default.
  const seasonPlaces = Object.values(season.season_pool_split || {})
    .filter(share => Number(share) > 0).length;

  // Both tables are CSS grids rather than tables so every row can share the panel height
  // evenly - a fixed row height leaves a gap at the bottom of a short field, and a table
  // that grows past the panel on a long one has nowhere to go on a screen with no scrollbar.
  const standingsColumns = {
    gridTemplateColumns: `calc(78 * var(--u)) minmax(0, 1fr) repeat(${weekCount}, calc(66 * var(--u))) calc(116 * var(--u))`
  };

  const holeCount = week && week.sessions && week.sessions[0] ? week.sessions[0].hole_count : 0;
  const finishedCards = netBoard.filter(entry => entry.is_complete).length;
  const cardsLabel = netBoard.length === 0
    ? null
    : [
      holeCount ? `${holeCount} holes` : null,
      `${netBoard.length} ${netBoard.length === 1 ? 'card' : 'cards'}`,
      finishedCards === netBoard.length ? 'all in' : `${finishedCards} finished`
    ].filter(Boolean).join(' · ');

  return (
    <div className="pgtv">
      <header className="pgtv-hero">
        <span className="pgtv-hero-image" style={{ backgroundImage: `url(${pingolfHero})` }} />
        <span className="pgtv-hero-scrim" />
        <div className="pgtv-hero-inner">
          <div>
            <p className="pgtv-hero-eyebrow">Capital Pinball Club</p>
            <p className="pgtv-hero-title">{season.name}</p>
          </div>
          <div className="pgtv-hero-right">
            <span className="pgtv-hero-pill">{progressLabel}</span>
            {stale && asOf && (
              <span className="pgtv-hero-pill pgtv-hero-pill--stale">Standings as of {formatSavedAt(asOf)}</span>
            )}
            {nextNight && (
              <span className="pgtv-hero-next">
                Week {nextNight.weekNumber} · {formatNextNight(new Date(nextNight.time))}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="pgtv-body">
        <section className="pgtv-standings">
          <div className="pgtv-panel-head">
            <p className="pgtv-panel-title">Season standings</p>
            <p className="pgtv-panel-note">
              Points by finishing position
              {weekCount > 0 && (dropCount > 0
                ? ` · best ${countingWeeks} of ${weekCount} weeks count`
                : ` · all ${weekCount} weeks count`)}
            </p>
          </div>

          <div className="pgtv-standings-cols" style={standingsColumns}>
            <span>#</span>
            <span>Player</span>
            {Array.from({ length: weekCount }, (unused, index) => (
              <span
                key={index}
                className={`pgtv-week-col${playedWeeks.has(index + 1) ? ' is-played' : ''}`}
              >
                W{index + 1}
              </span>
            ))}
            <span className="pgtv-col-total">Total</span>
          </div>

          <div className="pgtv-rows">
            {standings.length === 0 && (
              <p className="pgtv-empty">No cards turned in yet this season.</p>
            )}
            {standingsPage.slice.map(entry => (
              <div
                key={entry.userId}
                className={`pgtv-srow${entry.rank === 1 ? ' is-leader' : ''}`}
                style={standingsColumns}
              >
                <span className="pgtv-rank">{entry.rank}</span>
                <span className="pgtv-name">{playerName(entry)}</span>
                {(entry.weeklyPoints || []).map((points, index) => {
                  const played = playedWeeks.has(index + 1);
                  return (
                    <span
                      key={index}
                      className={`pgtv-week-col${played && points > 0 ? ' is-played' : ''}`}
                    >
                      {played ? points : '·'}
                    </span>
                  );
                })}
                <span className="pgtv-total">{entry.seasonTotal}</span>
              </div>
            ))}
          </div>

          <div className="pgtv-panel-foot">
            <p className="pgtv-panel-note">
              {seasonPlaces > 0 && weekCount > 0
                ? `Top ${seasonPlaces} at the end of Week ${weekCount} take the season pool`
                : 'Best weeks of the season decide the standings'}
            </p>
            {standingsPage.pages > 1 && (
              <div className="pgtv-paging">
                <span className="pgtv-paging-label">
                  Showing {standingsPage.start + 1}-{standingsPage.end} of {standingsPage.total}
                </span>
                <span className="pgtv-dots">
                  {Array.from({ length: standingsPage.pages }, (unused, index) => (
                    <span
                      key={index}
                      className={`pgtv-dot${index === standingsPage.page ? ' is-on' : ''}`}
                    />
                  ))}
                </span>
              </div>
            )}
          </div>
        </section>

        <section className="pgtv-week">
          <div className="pgtv-panel-head pgtv-week-head">
            <div className="pgtv-week-title">
              <p className="pgtv-panel-title">
                {week ? `Week ${week.week_number} results` : 'Week results'}
              </p>
              {week && (
                <span className={`pgtv-badge${isFinal ? ' is-final' : ' is-live'}`}>
                  {isFinal ? 'FINAL' : stage === 'in progress' ? 'LIVE' : 'UPCOMING'}
                </span>
              )}
            </div>
            {cardsLabel && <p className="pgtv-panel-note">{cardsLabel}</p>}
          </div>

          <div className="pgtv-week-cols">
            <span />
            <span />
            <span>Thru</span>
            <span>Card</span>
            <span>Hcp</span>
            <span>Net</span>
          </div>

          <div className="pgtv-rows">
            {netBoard.length === 0 && (
              <p className="pgtv-empty">
                {week
                  ? `Week ${week.week_number} has not been played yet.`
                  : 'No cards played yet this season.'}
              </p>
            )}
            {boardPage.slice.map(entry => (
              <div
                key={entry.user_id}
                className={`pgtv-wrow${entry.place === 1 ? ' is-leader' : ''}`}
              >
                <span className="pgtv-rank">{entry.place === null ? '-' : entry.place}</span>
                <span className="pgtv-name">{playerName(entry)}</span>
                <span className="pgtv-thru">{entry.is_complete ? 'F' : entry.holes_played}</span>
                <span className="pgtv-card">{formatVsPar(entry.total_score)}</span>
                <span className="pgtv-hcp">{formatHandicap(entry.playing_handicap)}</span>
                <span className="pgtv-net">{formatVsPar(entry.net)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {ticker.length > 0 && week && (
        <footer className="pgtv-ticker">
          <span className="pgtv-ticker-label">
            Week {week.week_number} · Course &amp; highlights
          </span>
          <div className="pgtv-ticker-track">
            {/* The rail carries the items twice and slides exactly half its width, so the loop
                closes on itself with no visible reset. */}
            <div
              className="pgtv-ticker-rail"
              style={{ animationDuration: `${Math.max(30, ticker.length * 3)}s` }}
            >
              {ticker.concat(ticker).map((item, index) => (
                <span key={index} className="pgtv-ticker-item">
                  {item.lead && <span className="pgtv-ticker-lead">{item.lead}</span>}
                  {item.chip && (
                    <span className={`pgtv-ticker-chip is-${item.tone}`}>{item.chip}</span>
                  )}
                  <span className="pgtv-ticker-main">{item.main}</span>
                  <span className="pgtv-ticker-sub">{item.sub}</span>
                </span>
              ))}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default PinGolfLeagueTv;
